import { useEffect, useState, useMemo } from "react";
import styles from "./styles.module.sass";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getTranslationValueByKey } from "../../utils";
import { getPathQuery } from "../../utils/pathHelper";
import {
  fetchPropertiesByAgentId,
  fetchPropertyById,
} from "../../state/actions/propertyActions";
import { setAgentFilter } from "../../state/actions/agentActions";
import { useCodeSearchContext } from "../../context/CodeSearchProvider";
import PageLayout from "../../components/PageLayout";
import { createPropertyColumns } from "../../constants/columnDefs/properties";
import { createColumnHelper } from "@tanstack/react-table";
import LoadingSpinner from "../../components/LoadingSpinner";
import { INCOMPLETE_FILTERS } from "../../constants/filters";

const STATUS_FILTERS = INCOMPLETE_FILTERS.map((f) => f.value);

const AgentPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const urlParam = params.id && params.id !== "all" ? params.id : null;
  const isStatusFilter = urlParam && STATUS_FILTERS.includes(urlParam);
  const propertyType = isStatusFilter ? null : urlParam;
  const filterQuery = useMemo(
    () => (isStatusFilter ? getPathQuery(`/home/${urlParam}`) : {}),
    [isStatusFilter, urlParam]
  );

  const agencies = useSelector((state) => state.property.agentProperties) || [];
  const currentProperty = useSelector(
    (state) => state.property.currentProperty
  );
  const currentUserId = useSelector((state) => state.account.user?.id);
  const agentFilter = useSelector((state) => state.admin.agentFilter);
  const agentPropertiesPagination = useSelector(
    (state) => state.property.agentPropertiesPagination
  );
  const [lastClicked, toggleLastClicked] = useState("agencies");
  const [loading, setLoading] = useState(false);
  const columnHelper = createColumnHelper();
  const [status, setStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  let properties = agencies;

  useEffect(() => {
    dispatch(setAgentFilter(urlParam));
  }, [dispatch, urlParam]);

  useEffect(() => {
    setLoading(true);
    if (currentUserId) {
      dispatch(
        fetchPropertiesByAgentId(currentUserId, {
          page: 1,
          per_page: perPage,
        })
      ).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, currentUserId]);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
    if (currentUserId) {
      dispatch(
        fetchPropertiesByAgentId(currentUserId, {
          ...(propertyType ? { category: propertyType } : {}),
          ...filterQuery,
          page: 1,
          per_page: perPage,
        })
      ).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, propertyType, filterQuery, currentUserId]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    setCurrentPage(newPage);
    dispatch(
      fetchPropertiesByAgentId(currentUserId, {
        ...(propertyType ? { category: propertyType } : {}),
        ...filterQuery,
        page: newPage,
        per_page: perPage,
      })
    ).finally(() => setLoading(false));
  };

  const handlePerPageChange = (newPerPage) => {
    setLoading(true);
    setPerPage(newPerPage);
    setCurrentPage(1);
    dispatch(
      fetchPropertiesByAgentId(currentUserId, {
        ...(propertyType ? { category: propertyType } : {}),
        ...filterQuery,
        page: 1,
        per_page: newPerPage,
      })
    ).finally(() => setLoading(false));
  };

  const handleVideoClick = (row) => {
    if (row.video !== "") {
      const url = row.video;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
    }
  };

  useEffect(() => {
    if (lastClicked != "code") {
      toggleLastClicked("agencies");
      propertiesMapped = agencies.map((property) => {
        return { ...property };
      });
    }
  }, [lastClicked]);

  const handleCodeSearchRef = useCodeSearchContext();

  const handleCodeSearch = async (selectedCode) => {
    setLoading(true);
    let retVal;
    if (selectedCode) {
      toggleLastClicked("code");
      retVal = await dispatch(fetchPropertyById(selectedCode, "admin"));
    } else {
      toggleLastClicked(null);
    }
    setStatus(!!retVal?.status);
    setLoading(false);
  };

  useEffect(() => {
    handleCodeSearchRef.current = {
      handleCodeSearch: handleCodeSearch,
    };
  }, []);

  const approvedPics = (pics) => {
    return pics?.find((el) => el.approved);
  };

  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  }, [agentFilter, properties]);

  const filterMap = {
    sold: (prop) => prop.sold,
    pictures: (prop) => !prop.pictures?.length,
    published: (prop) => prop.publish === false,
    deleted: (prop) => prop.deleted,
    propertyId: (prop) => prop.property_code === null,
    descriptionPublished: (prop) => !prop.description_published,
  };

  if (agentFilter) {
    const filterFn =
      filterMap[agentFilter] ||
      ((prop) => prop.type === getTranslationValueByKey(agentFilter));
    properties = properties.filter(filterFn);
  }

  let propertiesMapped = [];
  if (currentProperty?.property_code && lastClicked === "code" && status) {
    propertiesMapped = [{ ...currentProperty }];
  } else if (lastClicked != null) {
    propertiesMapped = properties.map((property) => ({ ...property }));
  }

  const columns = createPropertyColumns(
    columnHelper,
    navigate,
    handleVideoClick,
    approvedPics
  );

  return (
    <>
      {loading ? (
        <div>
          <LoadingSpinner />
        </div>
      ) : (
        <div className={styles.container}>
          {lastClicked != null && (
            <PageLayout
              dataArray={propertiesMapped}
              columns={columns}
              pagination={true}
              allowFilters={true}
              filters={{
                pagination: agentPropertiesPagination && {
                  currentPage,
                  totalCount: agentPropertiesPagination.total,
                  perPage,
                  lastPage: agentPropertiesPagination.lastPage,
                  setPerPage: handlePerPageChange,
                  action: (_, newPage) => handlePageChange(newPage),
                },
              }}
              props={{ propertiesMapped }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default AgentPage;
